import type { ReactElement } from 'react';
import type { Locale } from '@fastnorth/i18next';
import ProgressBar from '@fastnorth/kraken/components/progress-bar';
import { Div, Flex } from '@fastnorth/kraken/components/layout';
import formatProgressCount from '../utils/formatProgressCount';
import formatProgressDisplayValue from '../utils/formatProgressDisplayValue';
import formatProgressPercent from '../utils/formatProgressPercent';
import {
  passedCellBarVariants,
  passedCellCountVariants,
  passedCellPercentVariants,
  passedCellRootVariants,
} from './PassedCell.styles';

export type PassedCellProps = {
  countLabel: string;
  locale: Locale;
  percent: number | null;
};

const PassedCell = ({ countLabel, locale, percent }: PassedCellProps): ReactElement => {
  const barValue: number = percent ?? 0;
  const percentLabel: string =
    percent === null || percent === undefined
      ? formatProgressDisplayValue(null, locale)
      : formatProgressPercent(percent, locale);

  return (
    <Div className={passedCellRootVariants()}>
      <span className={passedCellCountVariants()}>{countLabel}</span>
      <Flex className="min-w-0 items-center gap-2">
        <Div className={passedCellBarVariants()}>
          <ProgressBar color="success" size="default" value={barValue} />
        </Div>
        <span className={passedCellPercentVariants()}>{percentLabel}</span>
      </Flex>
    </Div>
  );
};

export type PassedCellCheckpointProps = {
  locale: Locale;
  passedCount: number | null;
  passedPercentage: number | null;
  startedCount: number;
};

export const PassedCellCheckpoint = ({
  locale,
  passedCount,
  passedPercentage,
  startedCount,
}: PassedCellCheckpointProps): ReactElement => {
  const countLabel = `${formatProgressCount(passedCount, locale)} / ${formatProgressCount(startedCount, locale)}`;

  return <PassedCell countLabel={countLabel} locale={locale} percent={passedPercentage} />;
};

export type PassedCellInBetweenProps = {
  gapCount: number | null;
  gapPercentage: number | null;
  locale: Locale;
};

export const PassedCellInBetween = ({
  gapCount,
  gapPercentage,
  locale,
}: PassedCellInBetweenProps): ReactElement => {
  const countLabel: string = formatProgressCount(gapCount, locale);

  return <PassedCell countLabel={countLabel} locale={locale} percent={gapPercentage} />;
};

export default PassedCell;
